// apps/web/src/lib/vocabulary/SpreadVocabularyProvider.tsx
//
// Phase 9 Plan 04 Task 2 — Per-spread vocabulary Context provider (D-14).
//
// Shape mirrors apps/web/src/widgets/recipe-state-provider.tsx (the canonical
// context-provider pattern in apps/web): React.createContext<Value | null>(null),
// interface-first declaration, provider component wraps children with a typed
// Provider, React.useMemo for the value so consumers only re-render when the
// `value` prop reference changes.
//
// SSR-safety (Phase 13 share-link rendering) — the provider value is plain
// data; nothing reads from globals; no module-mutable singletons. Same
// `<SpreadVocabularyProvider value={vocabulary_overrides}>` wrap pattern
// renders identically on the server and the client (see RESEARCH §"useTerm
// SSR-safety analysis").
//
// SECURITY (T-09-04-01 trust boundary) — overrides values are user-supplied
// strings. useTerm returns them as plain strings; consumers MUST render via
// `{term}` JSX text-escaping. Plan 13 grep gate ensures no
// `dangerouslySetInnerHTML` wraps a useTerm result.
//
// Context is EXPORTED (not module-private as one RESEARCH sketch suggested) —
// rationale per Plan 09-04 plan body: exporting allows test files to render a
// mock provider directly without an additional re-export shim. The behavior
// shape `Partial<Record<VocabularySlot, string>>` keeps the type system honest.
import type { VocabularySlot } from "@the-recipe-spread/shared";
import * as React from "react";

export interface SpreadVocabularyContextValue {
  overrides: Partial<Record<VocabularySlot, string>>;
}

export const SpreadVocabularyContext = React.createContext<SpreadVocabularyContextValue | null>(
  null,
);

export interface SpreadVocabularyProviderProps {
  value: Partial<Record<VocabularySlot, string>>;
  children: React.ReactNode;
}

export function SpreadVocabularyProvider({
  value,
  children,
}: SpreadVocabularyProviderProps): React.ReactElement {
  // Memoize the context wrapper so consumers only re-render when `value`
  // reference changes. Callers feeding RHF-watched per-spread overrides into
  // this provider get the standard RHF immutability guarantee — `value`
  // references are recreated only when the form-state slice changes.
  const ctxValue = React.useMemo<SpreadVocabularyContextValue>(
    () => ({ overrides: value }),
    [value],
  );

  return (
    <SpreadVocabularyContext.Provider value={ctxValue}>{children}</SpreadVocabularyContext.Provider>
  );
}
